import { Divider } from "@mui/material";

import useProviderRecordEditable from "../../../../../../../../../hooks/useProviderRecordEditable";
import ProgressBarCircular from "../../../../../../../../ui library/dataVisualizations/ProgressBarCircular";
import AnalyticsHeader from "../../components/AnalyticsHeader";
import CustomTextField from "../../components/CustomTextField";
import { STRINGS } from "../../components/utils/strings";
import { useProCliStore } from "../../hooks/useProCliStore";

export default function AnalyticsStatus({
  currentAccordionItem,
  activateButtons,
  deactivateButtons,
}) {
  const { providersData, setProvidersData } = useProCliStore();
  const index = currentAccordionItem.index;
  const proCliData = currentAccordionItem.proCliData;
  const canEditRecord = useProviderRecordEditable(proCliData);
  const getTreatmentStatus = () => {
    if (
      providersData.get(index).editableFields &&
      "treatmentStatus" in providersData.get(index).editableFields
    ) {
      if (providersData.get(index).editableFields.treatmentStatus === true) {
        return true;
      }
      return false;
    } else if (proCliData.finishedTreatment === true) {
      return true;
    }
    return false;
  };

  const inTreatmentDays =
    proCliData.totalDaysInTreatment + STRINGS.SPACE_STR_SEPARATOR + STRINGS.DURATION_DAYS;
  const noBillsCount = proCliData.apptNoBillCount + STRINGS.SPACE_STR_SEPARATOR + STRINGS.APPT_TEXT;
  const scheduledApptCount =
    proCliData.scheduledApptCount + STRINGS.SPACE_STR_SEPARATOR + STRINGS.SCHEDULED_TEXT;
  const missedApptCount =
    proCliData.missedApptCount + STRINGS.SPACE_STR_SEPARATOR + STRINGS.MISSED_TEXT;
  const completedApptCount = proCliData.completedApptCount;
  const estimatedApptCount = getTreatmentStatus()
    ? proCliData.completedApptCount
    : proCliData.estimatedAppointmentCount;
  const medicalRecNo = proCliData.medicalRecordNumber;

  const getPercentCompletion = () => {
    const treatmentStat = getTreatmentStatus();
    if (treatmentStat) {
      return 100;
    }
    return Number(proCliData.completionPercentage) * 100 ?? 0;
  };

  const treatmentCompletionPercent = getPercentCompletion();

  const validateEstimatedApptCount = (value, setValue, setError, setHelperText) => {
    const regex = /[^0-9]/g;
    const match = regex.test(value);
    if (match) {
      deactivateButtons();
      setValue(value);
      setError(true);
      setHelperText("Only numbers allowed.");
      return 0;
    } else if (parseInt(value) > 32767) {
      setValue(value);
      setError(true);
      setHelperText("Count needs to be less than or equal to 32767");
      return 0;
    }
    setError(false);
    setHelperText("");
    const sanitizedValue = value.replace(regex, "");
    setValue(sanitizedValue);
    return 1;
  };

  const validateMedicalRecordNumber = (value, setValue, setError, setHelperText) => {
    const regex = /[^a-zA-Z0-9]/g;
    const match = regex.test(value);
    if (match) {
      setValue(value);
      setError(true);
      setHelperText("Only alphabets and numbers allowed");
      return 0;
    }

    const sanitizedValue = value.replace(regex, "");

    if (sanitizedValue.length > 40) {
      setValue(value);
      setError(true);
      setHelperText("The length needs to be less than 40");
      return 0;
    }
    setError(false);
    setHelperText("");
    const trimmedValue = sanitizedValue.slice(0, 40);
    setValue(trimmedValue);
    return 1;
  };

  const updateProviderInfo = (updatedData = null) => {
    if (updatedData === null) {
      deactivateButtons();
      return;
    }
    if (updatedData.get(index).editableFields === null) {
      deactivateButtons();
    } else {
      activateButtons();
    }
    if (updatedData !== null) {
      setProvidersData(updatedData);
    }
  };

  return (
    <div className="flex h-full flex-row flex-wrap p-2">
      <div className="mr-4 h-full">
        <ProgressBarCircular
          progressPercent={treatmentCompletionPercent > 100 ? 100 : treatmentCompletionPercent}
        />
      </div>
      <div className="flex flex-1 flex-col">
        <div className="mb-4 flex flex-row flex-wrap">
          <AnalyticsHeader title={inTreatmentDays} subTitle={"in Treatment"} />
          <Divider orientation="vertical" variant="middle" flexItem sx={{ borderWidth: "1px" }} />
          <AnalyticsHeader title={noBillsCount} subTitle={"With No-Bills"} />
          <Divider orientation="vertical" variant="middle" flexItem sx={{ borderWidth: "1px" }} />
          <AnalyticsHeader title={scheduledApptCount} subTitle={"Appointments"} />
          <Divider orientation="vertical" variant="middle" flexItem sx={{ borderWidth: "1px" }} />
          <AnalyticsHeader title={missedApptCount} subTitle={"Appointments"} />
        </div>
        <div className="mt-4 flex w-full flex-row flex-wrap justify-start">
          <div className="m-0 flex w-3/5 flex-row justify-start p-0">
            <CustomTextField
              index={index}
              title={"Completed Appt"}
              width={"w-5/6"}
              defaultValue={completedApptCount}
              providerInfo={proCliData}
              activateButtons={activateButtons}
              deactivateButtons={deactivateButtons}
              updateProviderInfo={updateProviderInfo}
              disabled={true}
            />
            <CustomTextField
              index={index}
              title={"Provider Estimated Count"}
              fieldName={"estimatedApptCount"}
              width={"w-5/6"}
              defaultValue={estimatedApptCount}
              providerInfo={proCliData}
              updateProviderInfo={updateProviderInfo}
              activateButtons={activateButtons}
              deactivateButtons={deactivateButtons}
              validatorFunction={validateEstimatedApptCount}
              disabled={!canEditRecord || getTreatmentStatus()}
            />
          </div>
          <div className="w-2/5 flex-1">
            <CustomTextField
              index={index}
              title={"Medical Record Number"}
              fieldName={"medicalRecNo"}
              width={"w-5/6"}
              defaultValue={medicalRecNo}
              providerInfo={proCliData}
              placeholder={"Add a number"}
              updateProviderInfo={updateProviderInfo}
              activateButtons={activateButtons}
              deactivateButtons={deactivateButtons}
              validatorFunction={validateMedicalRecordNumber}
              disabled={!canEditRecord}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
